import { type PropsWithChildren, useEffect } from 'react';

import { ComponentErrorBoundary } from '~/components/ComponentErrorBoundary';

// Add endpoints here as they become relevant
type ActionEndpoints =
  | '/create'
  | '/upgrade'
  | '/post_subscribe_form'
  | '/recommendations_subscribe_form'
  | '/create_login'
  | '/request_reset_password'
  | '/request_update_password'
  | '/admins/post_like'
  | '/post_like'
  | '/profile_and_email'
  | '/profile'
  | '/change_password'
  | '/update_subscription';

type Props = PropsWithChildren<{
  id?: string;
  fetcher: any;
  method: 'post' | 'patch';
  action: ActionEndpoints;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
  formRef?: React.RefObject<HTMLFormElement | null>;
}>;

const FormHelper = ({
  id,
  fetcher,
  children,
  method = 'post',
  action,
  onSubmit,
  onSuccess,
  onError,
  formRef,
}: Props) => {
  const fetcherData = fetcher.data;
  const isSuccess = fetcherData?.toast?.status === 'success' || fetcherData?.redirect || fetcherData?.ok === true;
  const isError = fetcherData?.toast?.status === 'error';
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (onSubmit) {
      e.preventDefault();
      onSubmit(e);
      return;
    }

    return true;
  };

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.(fetcherData);
    }

    if (isError) {
      onError?.(fetcherData);
    }
  }, [fetcherData]);

  return (
    <fetcher.Form
      className="contents"
      method={method}
      action={action}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => handleSubmit(e)}
      ref={formRef}
      id={id}
    >
      {children}
    </fetcher.Form>
  );
};

const ActionForm = ({ id, fetcher, children, method, action, onSubmit, onSuccess, onError, formRef }: Props) => {
  return (
    <ComponentErrorBoundary fallbackRender={() => <div>Something went wrong</div>}>
      <FormHelper
        id={id}
        fetcher={fetcher}
        method={method}
        action={action}
        onSubmit={onSubmit}
        onSuccess={onSuccess}
        onError={onError}
        formRef={formRef}
      >
        {children}
      </FormHelper>
    </ComponentErrorBoundary>
  );
};

export default ActionForm;
